import { Check, Minus, X } from "@phosphor-icons/react";
import {
  Button,
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
} from "@replicate/ui";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
  type GroupingState,
} from "@tanstack/react-table";
import classNames from "classnames";
import { capitalize } from "lodash-es";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { match } from "ts-pattern";
import { FilterControls } from "./filter-controls";
import { ModelBillingOverview } from "./overview";
import { PriceDisplay } from "./price-display";
import { TierHeader } from "./tier-header";
import type { GridViewProps } from "./types";
import { sortPrices } from "./utils";

const COLUMN_WIDTH = 140;
const FIRST_COLUMN_WIDTH = 200;
const LAST_COLUMN_WIDTH = 240;

function HeaderTitle({
  title,
  showTooltip,
}: { title: string; showTooltip?: boolean }) {
  const formattedTitle = capitalize(title);

  if (showTooltip) {
    return (
      <TooltipProvider>
        <TooltipAnchor className="z-10 cursor-help">
          <div className="font-semibold text-r8-sm text-r8-gray-11 line-clamp-2">
            {formattedTitle}
          </div>
        </TooltipAnchor>
        <Tooltip>
          {formattedTitle}
          <TooltipArrow />
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <div className="font-semibold text-r8-sm text-r8-gray-11 line-clamp-2">
      {formattedTitle}
    </div>
  );
}

interface TableShadowProps {
  showLeft: boolean;
  showRight: boolean;
}

function TableShadow({ showLeft, showRight }: TableShadowProps) {
  return (
    <>
      <div
        className={classNames(
          "z-[9] absolute top-0 bottom-0 w-4 pointer-events-none transition-opacity duration-1000",
          "bg-gradient-to-r from-black/10 dark:from-white/30 to-transparent",
          {
            "opacity-0 duration-300": !showLeft,
            "opacity-100 duration-1000": showLeft,
          }
        )}
        style={{ left: `${FIRST_COLUMN_WIDTH}px` }}
      />
      <div
        className={classNames(
          "z-[9] absolute top-0 bottom-0 w-4 pointer-events-none transition-opacity",
          "bg-gradient-to-l from-black/10 dark:from-white/30 to-transparent",
          {
            "opacity-0 duration-300": !showRight,
            "opacity-100 duration-1000": showRight,
          }
        )}
        style={{ right: `${LAST_COLUMN_WIDTH}px` }}
      />
    </>
  );
}

export function TableView({
  tiers,
  description,
  modelName,
}: GridViewProps): ReactNode {
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [filters, setFilters] = useState<Record<string, string | undefined>>(
    {}
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const checkScroll = useCallback(() => {
    if (!tableWrapperRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = tableWrapperRef.current;
    setShowLeftShadow(scrollLeft > 1);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    checkScroll();

    wrapper.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      wrapper.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  // Todo: swap to onMount
  setTimeout(() => {
    checkScroll();
  }, 100);

  // Early return if no tiers provided
  if (!tiers || tiers.length === 0) {
    return (
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-full xl:col-span-4">
          <ModelBillingOverview modelName={modelName} description={description}>
            <div className="space-y-4">
              <h3 className="text-r8-lg font-semibold">Filter by criteria</h3>
              <p className="text-r8-sm text-r8-gray-11">
                No pricing tiers available
              </p>
            </div>
          </ModelBillingOverview>
        </div>
        <div className="col-span-full xl:col-span-8">
          <div className="text-center py-8 text-r8-gray-11">
            No pricing information available for this model.
          </div>
        </div>
      </div>
    );
  }

  const columnHelper = createColumnHelper<(typeof tiers)[0]>();

  // Get all unique criteria keys from all tiers - memoized to prevent infinite re-renders
  const criteriaKeys = useMemo(() => {
    return Array.from(
      new Set(
        tiers.flatMap((tier) =>
          tier.criteria.map((criterion) => criterion.title)
        )
      )
    );
  }, [tiers]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: () => <HeaderTitle title="Tier" />,
        size: FIRST_COLUMN_WIDTH,
        cell: (info) => (
          <TierHeader
            title={info.getValue()}
            description={info.row.original.description}
          />
        ),
      }),
      // Add columns for each criterion
      ...criteriaKeys.map((key) =>
        columnHelper.accessor(
          (row) => {
            const criterion = row.criteria.find((c) => c.title === key);

            if (criterion) {
              return criterion.description;
            }

            return null;
          },
          {
            id: `criteria_${key}`, // Add unique ID to prevent conflicts
            header: () => <HeaderTitle title={key} showTooltip />,
            size: COLUMN_WIDTH, // Fixed width in pixels
            cell: (info) =>
              match(info.getValue())
                .with(null, () => (
                  <Minus size={30} className="fill-r8-gray-8" />
                ))
                .with("yes", () => (
                  <Check size={30} className="fill-r8-green-10" />
                ))
                .with("no", () => <X size={30} className="fill-r8-red-10" />)
                .otherwise((value) => value),
          }
        )
      ),
      columnHelper.accessor("prices", {
        header: () => <HeaderTitle title="Cost" />,
        size: LAST_COLUMN_WIDTH,
        cell: (info) => {
          return (
            <div
              className={`${
                info.getValue().length > 1 ? "grid grid-cols-2" : ""
              }`}
            >
              {sortPrices(info.getValue()).map((price, i) => (
                <div key={i} className="p-3">
                  <PriceDisplay
                    hideDescription
                    price={price}
                    single={info.getValue().length === 1}
                  />
                </div>
              ))}
            </div>
          );
        },
      }),
    ],
    [criteriaKeys, columnHelper.accessor]
  );

  // Filter the tiers based on the selected criteria - memoized to prevent constant re-filtering
  const filteredTiers = useMemo(() => {
    return tiers.filter((tier) => {
      if (tier.criteria.length === 0) {
        return true;
      }

      return Object.entries(filters).every(([filterKey, filterValue]) => {
        if (filterValue === undefined) {
          return true;
        }

        // TODO: Instead of filtering ranges by a match to the exact given
        // range, support filtering by any numeric value for a range, and find
        // the criteria for which that value is within the range.
        const criterion = tier.criteria.find((c) => c.title === filterKey);

        if (criterion === undefined) {
          return false;
        }

        return filterValue === criterion.description;
      });
    });
  }, [tiers, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  const hiddenResults = hasActiveFilters
    ? tiers.length - filteredTiers.length
    : 0;

  // Memoize the filter change callback to prevent unnecessary re-renders
  const handleFilterChange = useCallback(
    (key: string, value: string | undefined) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));

      // Scroll the grid container to top when filters change, but only if table is above sticky position
      if (gridRef.current) {
        const headerHeight =
          Number.parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--header-height"
            )
          ) * 16;
        const stickyOffset = headerHeight + 32; // header height + padding
        if (gridRef.current.getBoundingClientRect().top < stickyOffset) {
          window.scrollTo({
            // 32px is 2rem
            top: gridRef.current.offsetTop - headerHeight - 32,
            behavior: "instant",
          });
        }
      }
    },
    []
  );

  const clearFilters = useCallback(() => {
    for (const key in filters) {
      handleFilterChange(key, undefined);
    }
  }, [filters, handleFilterChange]);

  const table = useReactTable({
    data: filteredTiers || [],
    columns,
    state: {
      grouping,
    },
    onGroupingChange: setGrouping,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
  });

  return (
    <div className="grid grid-cols-12 gap-8" ref={gridRef}>
      <div className="col-span-full xl:col-span-4">
        <ModelBillingOverview description={description} modelName={modelName}>
          {criteriaKeys.length > 1 ? (
            <FilterControls
              tiers={tiers}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          ) : null}
        </ModelBillingOverview>
      </div>

      <div className="col-span-full xl:col-span-8">
        <div className="space-y-4">
          <div className="relative">
            <div
              ref={tableWrapperRef}
              className="overflow-x-auto border border-r8-gray-12"
            >
              {filteredTiers.length > 0 && (
                <table className="table-fixed w-full bg-white dark:bg-black max-h-[600px]">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        className="border-b border-r8-gray-8"
                      >
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={classNames(
                              "p-0 relative align-bottom bg-r8-gray-2",
                              {
                                sticky: !header.id.startsWith("criteria_"),
                                "left-0 z-[5]": header.id === "title",
                                "right-0 z-[5]": header.id === "prices",
                              }
                            )}
                            style={{
                              width: header.getSize(),
                              minWidth: header.getSize(),
                            }}
                          >
                            <div
                              className={classNames(
                                "absolute inset-0 pointer-events-none",
                                {
                                  "border-r": header.id !== "prices",
                                }
                              )}
                            />
                            <div className="p-3">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="group hover:bg-r8-gray-1 border-t border-r8-gray-6 relative z-[5] hover:z-[8] transition-all"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={classNames(
                              "p-0 relative align-top transition-all h-full",
                              {
                                "sticky bg-white dark:bg-black group-hover:bg-r8-gray-1":
                                  !cell.column.id.startsWith("criteria_"),
                                "left-0 z-[5]": cell.column.id === "title",
                                "right-0 z-[5]": cell.column.id === "prices",
                              }
                            )}
                            style={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                            }}
                          >
                            <div
                              className={classNames(
                                "absolute inset-0 pointer-events-none",
                                {
                                  "border-r": cell.column.id !== "prices",
                                  "border-r8-gray-3":
                                    cell.column.id.startsWith("criteria_"),
                                }
                              )}
                            />
                            <div
                              className={classNames("@container text-r8-sm", {
                                "p-3": cell.column.id !== "prices",
                                "align-center":
                                  cell.column.id.startsWith("criteria_"),
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!filteredTiers.length && (
                <div className="p-8 text-center text-r8-gray-11 bg-white dark:bg-black">
                  No results match your filters
                </div>
              )}
            </div>
            <TableShadow
              showLeft={showLeftShadow}
              showRight={showRightShadow}
            />
          </div>
          {hiddenResults > 0 && (
            <div className="bg-white dark:bg-black p-4 border flex items-center justify-between">
              <span className="text-r8-sm text-r8-gray-11">
                {hiddenResults} {hiddenResults === 1 ? "result" : "results"}{" "}
                hidden by filters
              </span>
              <Button size="sm" variant="filled" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
